import { useCallback } from "react";
import { useSession } from "@clerk/clerk-react";
import axios from "axios";
import { mcpServersStore } from "@/store/zustand/mcpServersStore";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import { getUnexpiredToken } from "@/utils/utils";

export function useMCPServers() {
  const { session } = useSession();

  const servers = mcpServersStore((state) => state.servers);
  const featuredMCPs = mcpServersStore((state) => state.featuredMCPs);
  const isLoading = mcpServersStore((state) => state.isLoading);
  const isFeaturedLoading = mcpServersStore((state) => state.isFeaturedLoading);
  const error = mcpServersStore((state) => state.error);
  const isModalOpen = mcpServersStore((state) => state.isModalOpen);

  const fetchServers = useCallback(async () => {
    if (!session) {
      console.warn("[MCP] No session available");
      return;
    }

    const store = mcpServersStore.getState();
    if (store.isLoading) return;

    store.setLoading(true);
    store.setError(null);

    try {
      const supabaseJWT = await getUnexpiredToken(session);

      if (!supabaseJWT) {
        throw new Error("Failed to get valid authentication token");
      }

      const response = await axios.get(
        `${import.meta.env.VITE_BACKEND_BASE_URL}/mcp/servers`,
        {
          headers: {
            Authorization: `Bearer ${supabaseJWT}`,
          },
        },
      );

      store.setServers(response.data.connections || []);
    } catch (error: any) {
      console.error("Failed to fetch MCP servers:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: `${import.meta.env.VITE_BACKEND_BASE_URL}/mcp/servers`,
      });
      store.setError(
        error.response?.data?.error ||
          error.message ||
          "Failed to fetch MCP servers",
      );
    }
  }, [session]);

  const fetchFeaturedMCPs = useCallback(async () => {
    const store = mcpServersStore.getState();
    if (store.isFeaturedLoading) return;

    store.setFeaturedLoading(true);

    try {
      const supabaseClient = useSupabaseStore.getState().supabaseClient;

      if (!supabaseClient) {
        throw new Error("Supabase client not initialized");
      }

      const { data, error } = await supabaseClient
        .from("featured_mcps")
        .select(
          "id, server_url, display_name, description, icon_url, docs_url, instructions, transport_type",
        )
        .eq("is_active", true)
        .order("display_order", { ascending: true });

      if (error) {
        console.error("Failed to fetch featured MCPs:", error);
        throw error;
      }

      store.setFeaturedMCPs(data || []);
    } catch (error: any) {
      console.error("Failed to fetch featured MCPs:", error);
      store.setFeaturedLoading(false);
    }
  }, []);

  const addServer = useCallback(
    async (
      serverUrl: string,
      serverName: string,
      transportType: "http" | "sse" = "http",
    ): Promise<{
      connectionId: string;
      requiresAuth: boolean;
      status: string;
      errorMessage?: string;
    }> => {
      if (!session) {
        throw new Error("No session available");
      }

      const supabaseJWT = await getUnexpiredToken(session);

      if (!supabaseJWT) {
        throw new Error("Failed to get valid authentication token");
      }

      const response = await axios.post(
        `${import.meta.env.VITE_BACKEND_BASE_URL}/mcp/servers/connect`,
        { serverUrl, serverName, transportType },
        {
          headers: {
            Authorization: `Bearer ${supabaseJWT}`,
          },
        },
      );

      return {
        connectionId: response.data.connectionId,
        requiresAuth: response.data.requiresAuth,
        status: response.data.status,
        errorMessage: response.data.errorMessage,
      };
    },
    [session],
  );

  const initiateOAuth = useCallback(
    async (connectionId: string): Promise<string> => {
      if (!session) {
        throw new Error("No session available");
      }

      const supabaseJWT = await getUnexpiredToken(session);

      if (!supabaseJWT) {
        throw new Error("Failed to get valid authentication token");
      }

      const response = await axios.post(
        `${import.meta.env.VITE_BACKEND_BASE_URL}/mcp/oauth/initiate`,
        { connectionId },
        {
          headers: {
            Authorization: `Bearer ${supabaseJWT}`,
          },
        },
      );

      return response.data.authorizationUrl;
    },
    [session],
  );

  const enableServer = useCallback(
    async (connectionId: string): Promise<void> => {
      if (!session) {
        throw new Error("No session available");
      }

      const supabaseJWT = await getUnexpiredToken(session);

      if (!supabaseJWT) {
        throw new Error("Failed to get valid authentication token");
      }

      await axios.post(
        `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/mcp/servers/${connectionId}/enable`,
        {},
        {
          headers: {
            Authorization: `Bearer ${supabaseJWT}`,
          },
        },
      );

      mcpServersStore.getState().updateServerEnabled(connectionId, true);
    },
    [session],
  );

  const disableServer = useCallback(
    async (connectionId: string): Promise<void> => {
      if (!session) {
        throw new Error("No session available");
      }

      const supabaseJWT = await getUnexpiredToken(session);

      if (!supabaseJWT) {
        throw new Error("Failed to get valid authentication token");
      }

      await axios.post(
        `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/mcp/servers/${connectionId}/disable`,
        {},
        {
          headers: {
            Authorization: `Bearer ${supabaseJWT}`,
          },
        },
      );

      mcpServersStore.getState().updateServerEnabled(connectionId, false);
    },
    [session],
  );

  const deleteServer = useCallback(
    async (connectionId: string): Promise<void> => {
      if (!session) {
        throw new Error("No session available");
      }

      const supabaseJWT = await getUnexpiredToken(session);

      if (!supabaseJWT) {
        throw new Error("Failed to get valid authentication token");
      }

      await axios.delete(
        `${import.meta.env.VITE_BACKEND_BASE_URL}/mcp/servers/${connectionId}`,
        {
          headers: {
            Authorization: `Bearer ${supabaseJWT}`,
          },
        },
      );

      await fetchServers();
    },
    [session, fetchServers],
  );

  return {
    servers,
    featuredMCPs,
    isLoading,
    isFeaturedLoading,
    error,
    isModalOpen,
    fetchServers,
    fetchFeaturedMCPs,
    addServer,
    initiateOAuth,
    enableServer,
    disableServer,
    deleteServer,
    clearServers: mcpServersStore.getState().clearServers,
    openModal: mcpServersStore.getState().openModal,
    closeModal: mcpServersStore.getState().closeModal,
  };
}
